import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ResponsiveClientLayout from "@/components/admin/ResponsiveClientLayout";
import SaldoWidget from "@/components/ui/SaldoWidget";
import TripleCalendar from "@/components/ui/TripleCalendar";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { Calendar as CalendarIcon, Clock, Building, DollarSign, Plus, Loader2, AlertCircle, Wallet, ChevronLeft, ChevronRight } from "lucide-react";
import { BlocoHorarioService } from "@/services/blocoHorarioService";
import reservaService from "@/services/reservaService";
import { CreditoService } from "@/services/creditoService";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useResponsive } from "@/hooks/useResponsive";

interface TimeSlot {
  id: string;
  sala_id: string;
  sala_nome: string;
  hora_inicio: string;
  hora_fim: string;
  valor: number;
  disponivel: boolean;
}

interface SelectedSlot extends TimeSlot {
  data: string;
}

interface SalaDisponibilidade {
  sala_id: string;
  sala_nome: string;
  sala_descricao?: string;
  blocos: TimeSlot[];
}

// ✅ COMPONENTE: Calendário Único para Mobile
interface SingleCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  isDayAvailable: (date: string) => { available: boolean; reason: string; type: string; allowClick?: boolean };
  currentMonth: Date;
  onMonthChange: (direction: 'prev' | 'next') => void;
}

const SingleCalendar: React.FC<SingleCalendarProps> = ({
  selectedDate,
  onDateSelect,
  isDayAvailable,
  currentMonth,
  onMonthChange
}) => {
  // Função para formatar data para string YYYY-MM-DD
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Função para obter nome do mês
  const getMonthName = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  // Função para gerar os dias de um mês
  const generateDaysInMonth = (monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    
    // Primeiro dia da semana (0 = domingo, 1 = segunda, etc.)
    const startDayOfWeek = firstDay.getDay();
    
    // Dias do mês anterior para completar a primeira semana
    const prevMonthDays = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const prevDay = new Date(year, month, -i);
      prevMonthDays.push({
        date: prevDay,
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }
    
    // Dias do mês atual
    const currentMonthDays = [];
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDay = new Date(year, month, day);
      currentMonthDays.push({
        date: currentDay,
        isCurrentMonth: true,
        isPrevMonth: false
      });
    }
    
    // Dias do próximo mês para completar as semanas
    const totalCells = 42; // 6 semanas × 7 dias
    const remainingCells = totalCells - (prevMonthDays.length + currentMonthDays.length);
    const nextMonthDays = [];
    for (let day = 1; day <= remainingCells; day++) {
      const nextDay = new Date(year, month + 1, day);
      nextMonthDays.push({
        date: nextDay,
        isCurrentMonth: false,
        isPrevMonth: false
      });
    }
    
    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  // Função para obter classes CSS do dia
  const getDayClasses = (dayInfo: { date: Date; isCurrentMonth: boolean }) => {
    const { date, isCurrentMonth } = dayInfo;
    const dateString = formatDateString(date);
    const validation = isDayAvailable(dateString);
    const isSelected = dateString === selectedDate;
    const isToday = dateString === formatDateString(new Date());
    
    let classes = 'w-10 h-10 flex items-center justify-center text-sm rounded-full cursor-pointer transition-all hover:bg-blue-50 ';
    
    if (!isCurrentMonth) {
      classes += 'text-gray-300 ';
    } else if (isSelected) {
      classes += 'bg-blue-600 text-white font-semibold ';
    } else if (isToday) {
      classes += 'bg-blue-100 text-blue-600 font-semibold ';
    } else if (!validation.available) {
      if (validation.type === 'feriado') {
        classes += 'bg-red-100 text-red-600 ';
      } else if (validation.type === 'passado') {
        classes += 'text-gray-400 ';
      } else if (validation.type === 'domingo') {
        classes += 'text-red-500 ';
      } else {
        classes += 'text-gray-400 ';
      }
    } else {
      classes += 'text-gray-700 hover:bg-blue-100 ';
    }
    
    return classes;
  };

  const days = generateDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header com navegação */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange('prev')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        <h3 className="text-lg font-semibold text-gray-800 capitalize">
          {getMonthName(currentMonth)}
        </h3>
        
        <button
          onClick={() => onMonthChange('next')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Header dos dias da semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dayName) => (
          <div key={dayName} className="h-10 flex items-center justify-center text-xs font-medium text-gray-500">
            {dayName}
          </div>
        ))}
      </div>
      
      {/* Grid dos dias */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, dayIndex) => {
          const dateString = formatDateString(dayInfo.date);
          const validation = isDayAvailable(dateString);
          
          return (
            <div
              key={dayIndex}
              className={getDayClasses(dayInfo)}
              onClick={() => {
                if (dayInfo.isCurrentMonth) {
                  onDateSelect(dateString);
                }
              }}
              title={!validation.available ? validation.reason : ''}
            >
              {dayInfo.date.getDate()}
            </div>
          );
        })}
      </div>
      
      {/* Legenda Mobile */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
          <span>Data selecionada</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-100 rounded-full"></div>
          <span>Hoje</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-100 rounded-full"></div>
          <span>Indisponível</span>
        </div>
      </div>
    </div>
  );
};

const ClientCalendar = () => {
  // ✅ CORREÇÃO: Hook de autenticação
  const { user, isAuthenticated } = useAuth();
  // ✅ NOVO: Hook para navegação
  const navigate = useNavigate();
  // ✅ RESPONSIVIDADE: Hook para detectar mobile
  const { isMobile } = useResponsive();

  // ✅ CORREÇÃO 1: Fix do Bug de Timezone
  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      .toLocaleDateString('pt-BR');
  };

  const [selectedDate, setSelectedDate] = useState(getLocalDateString(new Date()));
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [salasDisponibilidade, setSalasDisponibilidade] = useState<SalaDisponibilidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diasNaoFuncionais, setDiasNaoFuncionais] = useState<string[]>([]);
  
  // ✅ NOVO: Estado para controlar os 3 meses do calendário triplo
  const [startMonth, setStartMonth] = useState(new Date());
  
  // ✅ NOVO: Estado para controlar o modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // ✅ NOVO: Estado para controlar o modal de saldo insuficiente
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  
  // ✅ NOVO: Estado para controlar o toast de sucesso
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // ✅ CORREÇÃO 2: Validação de Regras de Negócio - CORRIGIDA
  // ✅ VALIDAÇÃO INTELIGENTE: Expandir range mas validar disponibilidade real
  const isDayAvailable = useCallback((date: string) => {
    
    
    const dateObj = new Date(date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    
    
    
    
  // ✅ MELHORIA: Permitir range expandido (hoje-2 até hoje+365)
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    minDate.setDate(today.getDate() - 2);
    
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
  maxDate.setDate(today.getDate() + 365);
    
    // Verificar se está fora do range permitido
    if (dateObj < minDate || dateObj > maxDate) {
      
      return { 
        available: false, 
        reason: dateObj < minDate ? 'Data muito antiga' : 'Data muito distante (máximo 365 dias)',
        type: 'range'
      };
    }
    
    // Verificar se é domingo (0 = domingo)
    if (dateObj.getDay() === 0) {
      
      return { available: false, reason: 'Não funcionamos aos domingos', type: 'domingo' };
    }
    
    // ✅ MELHORIA: Verificar se é dia passado (pode clicar mas não pode reservar)
    if (dateObj < today) {
      
      return { 
        available: false, 
        reason: 'Esta data já passou. Não é possível fazer novas reservas.', 
        type: 'passado',
        allowClick: true // ✅ NOVO: Permite clique mas mostra erro específico
      };
    }
    
    // Verificar se está na lista de dias não funcionais
    const isNaoFuncional = diasNaoFuncionais.includes(date);
    
    
    if (isNaoFuncional) {
      
      return { available: false, reason: 'Dia não funcional (feriado)', type: 'feriado' };
    }
    
    
    return { available: true, reason: '', type: 'disponivel' };
  }, [diasNaoFuncionais]);

  // ✅ NOVO: Controlar duração do toast (2 segundos)
  useEffect(() => {
    if (showSuccessToast) {
      const timer = setTimeout(() => {
        setShowSuccessToast(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessToast]);

  useEffect(() => {
    // ✅ CORREÇÃO: Usar rota pública para dias não funcionais com logs detalhados
    const fetchDiasNaoFuncionais = async () => {
      try {
        
        const response = await api.get('/api/dias-nao-funcionamento/publico');
        
        
        if (response.data && response.data.dias) {
          const diasFormatados = response.data.dias.map((d: { data: string }) => {
            // Garantir formato YYYY-MM-DD
            const data = new Date(d.data);
            const formatted = getLocalDateString(data);
            
            return formatted;
          });
          setDiasNaoFuncionais(diasFormatados);
          
          
        } else {
          
          setDiasNaoFuncionais([]);
        }
      } catch (error) {
        
        // Não é crítico, continua funcionando sem feriados
        setDiasNaoFuncionais([]);
      }
    };
    
    fetchDiasNaoFuncionais();
  }, []);



  // ✅ MELHORIA: Função para buscar disponibilidade com validação inteligente
  const fetchDisponibilidade = React.useCallback(async (data: string) => {
    
    
    const validation = isDayAvailable(data);
    
    
    // ✅ NOVO: Permitir busca mesmo para datas passadas (para mostrar que não há horários)
    if (!validation.available && !validation.allowClick) {
      setError(validation.reason);
      setSalasDisponibilidade([]);
      setTimeSlots([]);
      return;
    }
    
    // ✅ NOVO: Para datas passadas, mostrar erro mas permitir visualização
    if (!validation.available && validation.allowClick) {
      setError(validation.reason);
      // Continua a busca para mostrar que não há horários disponíveis
    } else {
      setError(null);
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await BlocoHorarioService.getDisponibilidadePorData(data);
      
      
      if (response.success && response.data) {
        const salas = response.data as SalaDisponibilidade[];
        setSalasDisponibilidade(salas);
        
        // Criar lista flat de slots para compatibilidade
        const allSlots: TimeSlot[] = [];
        salas.forEach(sala => {
          allSlots.push(...sala.blocos);
        });
        setTimeSlots(allSlots);
        
        
      } else {
        setError('Nenhum horário disponível para esta data');
        setSalasDisponibilidade([]);
        setTimeSlots([]);
      }
    } catch (err) {
      
      setError('Erro ao carregar horários disponíveis');
      setSalasDisponibilidade([]);
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  }, [isDayAvailable]);

  // Buscar disponibilidade quando a data mudar
  useEffect(() => {
    if (selectedDate) {
      fetchDisponibilidade(selectedDate);
    }
  }, [selectedDate, diasNaoFuncionais, fetchDisponibilidade]); // Adicionar diasNaoFuncionais como dependência

  // Função para abrir modal de confirmação
  const handleOpenConfirmModal = async () => {
    if (selectedSlots.length === 0) {
      setError('Selecione pelo menos um horário');
      return;
    }
    
    // Verificar se o usuário tem saldo suficiente
    try {
      const saldoAtual = await CreditoService.getSaldoUsuario(user!.id);
      const totalValue = selectedSlots.reduce((sum, slot) => sum + slot.valor, 0);
      
      if (saldoAtual < totalValue) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      
      setShowConfirmModal(true);
    } catch (error) {
      
      setError('Erro ao verificar saldo. Tente novamente.');
    }
  };

  // Função para criar reserva (chamada após confirmação)
  const handleCreateReserva = async () => {
    
    setLoading(true);
    setError(null);

    try {
      // ✅ CORREÇÃO: Usar hook de autenticação ao invés de localStorage
      if (!isAuthenticated || !user?.id) {
        setError('Usuário não autenticado');
        setShowConfirmModal(false);
        return;
      }

      const userId = user.id;

      // Criar reservas para cada slot selecionado
      const reservasPromises = selectedSlots.map(slot => {
        const reservaData = {
          user_id: userId,
          sala_id: slot.sala_id,
          bloco_id: slot.id,
          data: `${slot.data}T12:00:00.000Z`, // Ajustar conforme necessário
          pago_com_credito: true, // ✅ CORRIGIDO: Usar créditos da carteira
          valor_credito_usado: slot.valor
        };
        
        
        return reservaService.create(reservaData);
      });

      const reservasCriadas = await Promise.all(reservasPromises);
      

      // Fechar modal e limpar seleções
      setShowConfirmModal(false);
      setSelectedSlots([]);
      
      // ✅ NOVO: Redirecionar para página de reservas após sucesso
      
      navigate('/client/reservations', { 
        replace: true,
        state: { 
          message: `✅ Reserva${selectedSlots.length > 1 ? 's' : ''} criada${selectedSlots.length > 1 ? 's' : ''} com sucesso!`,
          type: 'success'
        }
      });

    } catch (err) {
      
      setError('Erro ao criar reservas. Tente novamente.');
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Função para lidar com o modal de saldo insuficiente
  const handleGoToCredits = () => {
    setShowInsufficientBalanceModal(false);
    navigate('/client/creditos');
  };

  const handleCancelInsufficientBalance = () => {
    setShowInsufficientBalanceModal(false);
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    if (!slot.disponivel) return;
    
    const selectedSlot: SelectedSlot = {
      ...slot,
      data: selectedDate
    };
    
    const isAlreadySelected = selectedSlots.some(s => s.id === slot.id && s.data === selectedDate);
    
    if (isAlreadySelected) {
      setSelectedSlots(selectedSlots.filter(s => !(s.id === slot.id && s.data === selectedDate)));
    } else {
      setSelectedSlots([...selectedSlots, selectedSlot]);
    }
  };

  const isSlotSelected = (slotId: string) => {
    return selectedSlots.some(s => s.id === slotId && s.data === selectedDate);
  };

  // ✅ NOVO: Funções para o calendário triplo
  const handleDateSelect = (date: string) => {
    
    setSelectedDate(date);
    // Limpar slots selecionados ao mudar de data - REMOVIDO PARA PERSISTIR SELEÇÃO
    // setSelectedSlots([]); 
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    const newStartMonth = new Date(startMonth);
    if (direction === 'prev') {
      newStartMonth.setMonth(startMonth.getMonth() - 1);
    } else {
      newStartMonth.setMonth(startMonth.getMonth() + 1);
    }
    setStartMonth(newStartMonth);
  };

  const totalValue = selectedSlots.reduce((sum, slot) => sum + slot.valor, 0);

  // Usar dados das salas da API ao invés de agrupar timeSlots
  const groupedSlots = salasDisponibilidade.reduce((acc, sala) => {
    acc[sala.sala_nome] = sala.blocos;
    return acc;
  }, {} as Record<string, TimeSlot[]>);

  // ✅ MELHORIA: Range expandido do date picker (hoje-2 até hoje+365)
  const todayForPicker = new Date();
  todayForPicker.setHours(0, 0, 0, 0);
  
  // ✅ NOVO: Permitir seleção de hoje-2
  const minDateForPicker = new Date();
  minDateForPicker.setHours(0, 0, 0, 0);
  minDateForPicker.setDate(todayForPicker.getDate() - 2);
  
  const maxDateForPicker = new Date();
  maxDateForPicker.setHours(0, 0, 0, 0);
  maxDateForPicker.setDate(todayForPicker.getDate() + 365);

  const minDateString = getLocalDateString(minDateForPicker);
  const maxDateString = getLocalDateString(maxDateForPicker);
  
  
  
  
  
  
  

  return (
    <ResponsiveClientLayout
      title="Calendário de Reservas"
      subtitle="Selecione uma data e horários disponíveis para suas sessões"
    >
      <div className="space-y-6">
        {/* ✅ MELHORIA: Saldo Widget sempre visível */}
        {/* <SaldoWidget 
          size="medium" 
          showActions={true}
          className="mb-6"
        /> */}



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Coluna 1: Calendário */}
            <div className="lg:col-span-1">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5" />
                            Selecionar Data
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <SingleCalendar
                            selectedDate={selectedDate}
                            onDateSelect={handleDateSelect}
                            isDayAvailable={isDayAvailable}
                            currentMonth={startMonth}
                            onMonthChange={handleMonthChange}
                        />
                        <div className="mt-4 flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                Data selecionada: <span className="font-medium">{formatDisplayDate(selectedDate)}</span>
                            </p>
                        </div>
                        {!isDayAvailable(selectedDate).available && (
                            <div className="mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <AlertCircle className="w-4 h-4 text-red-600" />
                                <span className="text-sm text-red-700">{isDayAvailable(selectedDate).reason}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Coluna 2: Horários Disponíveis */}
            <div className="lg:col-span-1 space-y-6">
                {loading && (
                    <Card>
                        <CardContent className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin mr-2" />
                            <span>Carregando...</span>
                        </CardContent>
                    </Card>
                )}
                {error && !loading && (
                    <Card>
                        <CardContent className="py-8 text-center text-red-600">
                            <p>{error}</p>
                            <Button onClick={() => fetchDisponibilidade(selectedDate)} className="mt-2" variant="outline" size="sm">Tentar novamente</Button>
                        </CardContent>
                    </Card>
                )}
                {!loading && !error && Object.entries(groupedSlots).length === 0 && (
                    <Card>
                        <CardContent className="py-8 text-center text-gray-500">
                            <p>Nenhum horário disponível para esta data.</p>
                        </CardContent>
                    </Card>
                )}
                {!loading && !error && Object.entries(groupedSlots).map(([roomName, slots]) => (
                    <Card key={roomName}>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building className="w-5 h-5" />
                                {roomName}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                {slots.map((slot) => (
                                    <div
                                        key={slot.id}
                                        onClick={() => handleSlotSelect(slot)}
                                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                            !slot.disponivel 
                                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                                                : isSlotSelected(slot.id)
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-sm">{slot.hora_inicio} - {slot.hora_fim}</span>
                                            {isSlotSelected(slot.id) && (
                                                <Badge variant="default" className="bg-green-100 text-green-800 text-xs">✓</Badge>
                                            )}
                                        </div>
                                        <span className="text-md font-bold text-blue-600">R$ {slot.valor.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Coluna 3: Resumo da Reserva e Informações */}
            <div className="lg:col-span-1 space-y-6">
                <Card className="sticky top-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Resumo da Reserva
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedSlots.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Nenhum horário selecionado</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    {selectedSlots.map((slot) => (
                                        <div key={`${slot.id}-${slot.data}`} className="p-3 bg-blue-50 rounded-lg">
                                            <div className="flex justify-between items-start mb-1">
                                                <p className="font-medium text-sm text-blue-900">{slot.sala_nome}</p>
                                                <Button variant="ghost" size="sm" onClick={() => handleSlotSelect(slot)} className="text-red-600 hover:text-red-700 p-1 h-auto">✕</Button>
                                            </div>
                                            <p className="text-xs text-blue-700">{formatDisplayDate(slot.data)} • {slot.hora_inicio} - {slot.hora_fim}</p>
                                            <p className="text-sm font-bold text-blue-900">R$ {slot.valor.toFixed(2)}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="border-t pt-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="font-medium">Total:</span>
                                        <span className="text-xl font-bold text-blue-600">R$ {totalValue.toFixed(2)}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleOpenConfirmModal} disabled={loading || selectedSlots.length === 0}>
                                            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processando...</> : <><Wallet className="w-4 h-4 mr-2" />Reservar com Saldo</>}
                                        </Button>
                                        <Button variant="outline" className="w-full" onClick={() => setSelectedSlots([])}>Limpar Seleção</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card className="mt-6">
                    <CardHeader><CardTitle className="text-lg">Informações Importantes</CardTitle></CardHeader>
                    <CardContent className="text-xs text-gray-600 space-y-2">
                        <p>• O pagamento é feito com o seu saldo de créditos.</p>
                        <p>• Cancelamento com mais de 48h tem reembolso de 100%.</p>
                        <p>• Cancelamento entre 24h e 48h tem reembolso de 50%.</p>
                        <p>• Cancelamentos com menos de 24h não têm reembolso.</p>
                    </CardContent>
                </Card>
            </div>
        </div>
      </div>

      {/* Modal de Saldo Insuficiente */}
      {showInsufficientBalanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Saldo Insuficiente</h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              Seu saldo atual não é suficiente para realizar esta reserva. 
              Você precisa adicionar mais créditos à sua carteira para continuar.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={handleGoToCredits}
              >
                <Wallet className="w-4 h-4 mr-2" />
                Adicionar Créditos
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleCancelInsufficientBalance}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleCreateReserva}
        title="Confirmar Reserva"
        message="Você tem certeza que deseja confirmar esta reserva? O valor será debitado do seu saldo da carteira."
        reservationDetails={{
          selectedSlots: selectedSlots,
          totalValue: totalValue,
          selectedDate: selectedDate
        }}
        isLoading={loading}
      />

      {/* Toast de Sucesso */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="font-medium">Reserva criada com sucesso!</span>
          </div>
        </div>
      )}
    </ResponsiveClientLayout>
  );
};

export default ClientCalendar; 
